import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, LogOut, Upload } from "lucide-react";
import { api, authHeaders, money } from "../lib/api";

const EMPTY_PRODUCT = {
  name: "", brand: "", era: "1990s", category: "", image: "", tile_color: "#FFD84D",
  price: "", retailer: "", affiliate_url: "", tagline: "", why_died: "", why_back: "",
  stat_value: "", stat_label: "", stat_source: "", set_id: "", rare: false, staff_pick: false,
};
const EMPTY_SET = { name: "", tagline: "", description: "", color: "#2B5CE6", product_ids: [] };

const inputCls = "w-full rounded-xl border-2 border-charcoal bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue";

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] font-bold uppercase text-charcoal/60">{label}</span>
      {children}
    </label>
  );
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sr_admin_token", data.token);
      onSuccess(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login" className="flex min-h-[70vh] items-center justify-center bg-cream px-4 py-16">
      <form onSubmit={submit} className="card-chunky w-full max-w-sm p-8">
        <p className="font-mono text-xs font-bold uppercase text-charcoal/60">Restricted area</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold uppercase tracking-tight">Admin login</h1>
        <div className="mt-6 flex flex-col gap-4">
          <Field label="Email">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className={inputCls} />
          </Field>
          <Field label="Password">
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className={inputCls} />
          </Field>
          {error && <p data-testid="admin-login-error" className="rounded-lg border-2 border-coral bg-coral/10 px-3 py-2 font-mono text-xs text-coral">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="btn-chunky bg-charcoal px-6 py-3 text-sm text-cream hover:bg-blue disabled:opacity-50">
            {busy ? "Checking..." : "Log in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ProductForm({ initial, sets, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, price: Number(form.price), set_id: form.set_id || null });
  };
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, authHeaders());
      set("image", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed. Try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <form onSubmit={submit} data-testid="product-form" className="card-chunky mt-6 p-6">
      <h3 className="font-display text-xl font-extrabold uppercase">{initial.id ? "Edit product" : "New product"}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className={inputCls} /></Field>
        <Field label="Brand"><input required value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" className={inputCls} /></Field>
        <Field label="Era">
          <select value={form.era} onChange={(e) => set("era", e.target.value)} data-testid="pf-era" className={inputCls}>
            <option>1980s</option><option>1990s</option><option>2000s</option>
          </select>
        </Field>
        <Field label="Category"><input required value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className={inputCls} placeholder="Audio" /></Field>
        <Field label="Price (USD)"><input required type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" className={inputCls} /></Field>
        <Field label="Retailer"><input required value={form.retailer} onChange={(e) => set("retailer", e.target.value)} data-testid="pf-retailer" className={inputCls} placeholder="eBay" /></Field>
        <Field label="Affiliate URL"><input required type="url" value={form.affiliate_url} onChange={(e) => set("affiliate_url", e.target.value)} data-testid="pf-affiliate-url" className={inputCls} placeholder="https://..." /></Field>
        <Field label="Image URL"><input required type="url" value={form.image} onChange={(e) => set("image", e.target.value)} data-testid="pf-image" className={inputCls} placeholder="https://..." /></Field>
        <Field label="Tile colour">
          <select value={form.tile_color} onChange={(e) => set("tile_color", e.target.value)} data-testid="pf-tile-color" className={inputCls}>
            <option value="#FFD84D">Acid Yellow</option><option value="#2B5CE6">Electric Blue</option>
            <option value="#FF4E42">Hot Coral</option><option value="#4FD1A5">Mint</option><option value="#FDF6E8">Warm Cream</option>
          </select>
        </Field>
        <Field label="Starter Pack (set)">
          <select value={form.set_id || ""} onChange={(e) => set("set_id", e.target.value)} data-testid="pf-set" className={inputCls}>
            <option value="">No set</option>
            {sets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Tagline"><input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="pf-tagline" className={inputCls} /></Field>
        <Field label="Stat value"><input value={form.stat_value} onChange={(e) => set("stat_value", e.target.value)} data-testid="pf-stat-value" className={inputCls} placeholder="130M" /></Field>
        <Field label="Stat label"><input value={form.stat_label} onChange={(e) => set("stat_label", e.target.value)} data-testid="pf-stat-label" className={inputCls} /></Field>
        <Field label="Stat source"><input value={form.stat_source} onChange={(e) => set("stat_source", e.target.value)} data-testid="pf-stat-source" className={inputCls} placeholder="Nintendo" /></Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4 rounded-2xl border-2 border-dashed border-charcoal/40 p-4">
        {form.image && (
          <img src={form.image} alt="Product preview" data-testid="pf-image-preview" className="h-16 w-16 rounded-xl border-2 border-charcoal object-cover" />
        )}
        <label className="btn-chunky cursor-pointer bg-white px-5 py-2.5 text-sm hover:bg-mint">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload image file"}
          <input type="file" accept="image/*" onChange={uploadImage} data-testid="pf-image-upload" className="hidden" />
        </label>
        <span className="font-mono text-xs text-charcoal/60">
          Upload a file or paste a URL above — uploaded images are stored permanently, no link needed.
        </span>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Why it died"><textarea rows={3} value={form.why_died} onChange={(e) => set("why_died", e.target.value)} data-testid="pf-why-died" className={inputCls} /></Field>
        <Field label="Why it's back"><textarea rows={3} value={form.why_back} onChange={(e) => set("why_back", e.target.value)} data-testid="pf-why-back" className={inputCls} /></Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input type="checkbox" checked={form.rare} onChange={(e) => set("rare", e.target.checked)} data-testid="pf-rare" className="h-4 w-4 accent-coral" /> Rare find
        </label>
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input type="checkbox" checked={form.staff_pick} onChange={(e) => set("staff_pick", e.target.checked)} data-testid="pf-staff-pick" className="h-4 w-4 accent-blue" /> Staff pick
        </label>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" data-testid="pf-save" className="btn-chunky bg-mint px-6 py-2.5 text-sm">Save</button>
        <button type="button" onClick={onCancel} data-testid="pf-cancel" className="btn-chunky bg-white px-6 py-2.5 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function SetForm({ initial, products, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (pid) =>
    set("product_ids", form.product_ids.includes(pid) ? form.product_ids.filter((x) => x !== pid) : [...form.product_ids, pid]);
  const submit = (e) => { e.preventDefault(); onSave(form); };
  return (
    <form onSubmit={submit} data-testid="set-form" className="card-chunky mt-6 p-6">
      <h3 className="font-display text-xl font-extrabold uppercase">{initial.id ? "Edit set" : "New set"}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Name"><input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="sf-name" className={inputCls} /></Field>
        <Field label="Tagline"><input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="sf-tagline" className={inputCls} /></Field>
        <Field label="Colour">
          <select value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="sf-color" className={inputCls}>
            <option value="#2B5CE6">Electric Blue</option><option value="#FF4E42">Hot Coral</option>
            <option value="#FFD84D">Acid Yellow</option><option value="#4FD1A5">Mint</option>
          </select>
        </Field>
        <Field label="Description"><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="sf-description" className={inputCls} /></Field>
      </div>
      <p className="mt-4 font-mono text-[10px] font-bold uppercase text-charcoal/60">Products in this set (2-5)</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <label key={p.id} className="flex items-center gap-2 rounded-xl border-2 border-charcoal bg-white px-3 py-2 text-sm">
            <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} data-testid={`sf-product-${p.id}`} className="h-4 w-4 accent-blue" />
            <span className="truncate">{p.name}</span>
          </label>
        ))}
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" data-testid="sf-save" className="btn-chunky bg-mint px-6 py-2.5 text-sm">Save</button>
        <button type="button" onClick={onCancel} data-testid="sf-cancel" className="btn-chunky bg-white px-6 py-2.5 text-sm">Cancel</button>
      </div>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("sr_admin_token"));
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [sets, setSets] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingSet, setEditingSet] = useState(null);
  const [subscribers, setSubscribers] = useState([]);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/sets").then((r) => setSets(r.data)).catch(() => {});
  }, []);

  const loadSubscribers = useCallback(() => {
    api.get("/admin/subscribers", authHeaders()).then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (token) {
      api.get("/auth/me", authHeaders()).then(load).catch(() => {
        localStorage.removeItem("sr_admin_token");
        setToken(null);
      });
    }
  }, [token, load]);

  if (!token) return <LoginForm onSuccess={setToken} />;

  const logout = () => {
    localStorage.removeItem("sr_admin_token");
    setToken(null);
  };

  const saveProduct = async (form) => {
    try {
      if (form.id) await api.put(`/admin/products/${form.id}`, form, authHeaders());
      else await api.post("/admin/products", form, authHeaders());
      toast.success("Product saved");
      setEditingProduct(null);
      load();
    } catch {
      toast.error("Could not save product");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`, authHeaders());
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Could not delete product");
    }
  };

  const saveSet = async (form) => {
    try {
      if (form.id) await api.put(`/admin/sets/${form.id}`, form, authHeaders());
      else await api.post("/admin/sets", form, authHeaders());
      toast.success("Set saved");
      setEditingSet(null);
      load();
    } catch {
      toast.error("Could not save set");
    }
  };

  const deleteSet = async (id) => {
    if (!window.confirm("Delete this set?")) return;
    try {
      await api.delete(`/admin/sets/${id}`, authHeaders());
      toast.success("Set deleted");
      load();
    } catch {
      toast.error("Could not delete set");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs font-bold uppercase text-charcoal/60">Stay Retro HQ</p>
            <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight">Catalogue admin</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-chunky bg-white px-5 py-2.5 text-sm hover:bg-coral hover:text-white">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        <div className="mt-8 flex gap-2">
          {["products", "sets", "subscribers"].map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setEditingProduct(null); setEditingSet(null); if (t === "subscribers") loadSubscribers(); }}
              data-testid={`admin-tab-${t}`}
              className={`rounded-full border-2 border-charcoal px-6 py-2.5 font-display text-sm font-bold uppercase ${tab === t ? "bg-blue text-white shadow-hard-sm" : "bg-white"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="mt-8">
            <button onClick={() => setEditingProduct({ ...EMPTY_PRODUCT })} data-testid="admin-add-product" className="btn-chunky bg-yellow px-5 py-2.5 text-sm">
              <Plus className="h-4 w-4" /> Add product
            </button>
            {editingProduct && (
              <ProductForm initial={editingProduct} sets={sets} onSave={saveProduct} onCancel={() => setEditingProduct(null)} />
            )}
            <div className="mt-6 flex flex-col gap-3">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="card-chunky flex flex-wrap items-center gap-4 p-4">
                  <img src={p.image} alt="" className="h-14 w-14 rounded-xl border-2 border-charcoal object-cover" style={{ backgroundColor: p.tile_color }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-extrabold uppercase">{p.name}</p>
                    <p className="font-mono text-xs text-charcoal/60">{p.brand} / {p.era} / {p.category} / {money(p.price)} / {p.retailer}</p>
                  </div>
                  <div className="flex gap-2">
                    {p.rare && <span className="rounded-full border-2 border-charcoal bg-yellow px-2 py-0.5 font-mono text-[10px] font-bold uppercase">Rare</span>}
                    {p.staff_pick && <span className="rounded-full border-2 border-charcoal bg-blue px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-white">Pick</span>}
                    <button onClick={() => setEditingProduct({ ...p, set_id: p.set_id || "" })} data-testid={`admin-edit-product-${p.id}`} className="rounded-full border-2 border-charcoal bg-white p-2 shadow-hard-sm hover:bg-mint" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteProduct(p.id)} data-testid={`admin-delete-product-${p.id}`} className="rounded-full border-2 border-charcoal bg-white p-2 shadow-hard-sm hover:bg-coral hover:text-white" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "sets" && (
          <div className="mt-8">
            <button onClick={() => setEditingSet({ ...EMPTY_SET })} data-testid="admin-add-set" className="btn-chunky bg-yellow px-5 py-2.5 text-sm">
              <Plus className="h-4 w-4" /> Add set
            </button>
            {editingSet && (
              <SetForm initial={editingSet} products={products} onSave={saveSet} onCancel={() => setEditingSet(null)} />
            )}
            <div className="mt-6 flex flex-col gap-3">
              {sets.map((s) => (
                <div key={s.id} data-testid={`admin-set-row-${s.id}`} className="card-chunky flex flex-wrap items-center gap-4 p-4">
                  <span className="h-14 w-14 rounded-xl border-2 border-charcoal" style={{ backgroundColor: s.color }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-extrabold uppercase">{s.name}</p>
                    <p className="font-mono text-xs text-charcoal/60">{s.product_ids.length} pieces</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingSet({ ...s })} data-testid={`admin-edit-set-${s.id}`} className="rounded-full border-2 border-charcoal bg-white p-2 shadow-hard-sm hover:bg-mint" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => deleteSet(s.id)} data-testid={`admin-delete-set-${s.id}`} className="rounded-full border-2 border-charcoal bg-white p-2 shadow-hard-sm hover:bg-coral hover:text-white" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "subscribers" && (
          <div className="mt-8" data-testid="admin-subscribers">
            <p className="font-mono text-xs font-bold uppercase text-charcoal/60">
              {subscribers.length} collectors on the drop list
            </p>
            <div className="mt-4 flex flex-col gap-2">
              {subscribers.map((s) => (
                <div key={s.email} data-testid={`subscriber-row-${s.email}`} className="card-chunky flex items-center justify-between p-4">
                  <span className="font-mono text-sm">{s.email}</span>
                  <span className="font-mono text-xs text-charcoal/50">{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
              ))}
              {subscribers.length === 0 && (
                <p className="font-mono text-sm text-charcoal/60">No subscribers yet. Share the link.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
